import { useCardData } from 'components/providers/CardDataProvider'
import { getPurchasesOfPurchaseEvent } from 'lib/api'
import { useCallback, useMemo } from 'react'
import {
  Article,
  ArticleParams,
  Card,
  CardmarketCondition,
  CountryEdition,
  countryEditionMap,
  GRADING_COMPANY_LABELS,
  mapBackendFinishAttributeToInternal,
  PurchaseArticle,
  SPLITTING_STRATEGY,
} from 'shared'

export const useFetchPurchaseArticles = () => {
  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  const fetchArticles = useCallback(
    async (purchaseEventId: number): Promise<PurchaseArticle[]> => {
      const rawData: any[] = await getPurchasesOfPurchaseEvent(purchaseEventId)

      const articles: PurchaseArticle[] = []

      for (const d of rawData) {
        const attributes: ArticleParams['attributes'] = [
          Number(d.quantity),
          d.ptProduct.condition as CardmarketCondition,
          Number(d.ptProduct.idLanguage),
          Number(d.ptProduct.mkmProductId),
          Number(d.price),
          '',
          false, // isPlayset
          d.ptProduct.isSigned,
          d.ptProduct.isFirstEd,
          '', // idArticle
          null, // listedAt
          false, // isAltered
          GRADING_COMPANY_LABELS.find((entry) => entry.code === d.ptProduct.gradingCompany)?.code ??
            undefined, // grading provider
          d.ptProduct.ptProductGrading?.grade ?? undefined,
          countryEditionMap.find((entry) => entry.code === d.ptProduct.countryEdition)?.value ??
            CountryEdition.Regular,
          mapBackendFinishAttributeToInternal(d.ptProduct.finishType),
          undefined,
        ]

        const article = new Article({ attributes })

        const card = cardDataMap.get(article.idProduct)

        if (card) {
          articles.push(
            new PurchaseArticle({
              attributes,
              card: card.toPlainObject(),
              ptProductId: Number(d.ptProduct.id),
              purchaseEventId: Number(d.purchaseEventId),
              buyPrice: d.price,
              purchaseId: Number(d.id),
              isVat: d.isVat,
              priceLock: Number(d.creationCause) === SPLITTING_STRATEGY.MANUAL,
            })
          )
        }
      }

      return articles.sort((a, b) => a.card.name.localeCompare(b.card.name))
    },
    [cardDataMap]
  )

  return { fetchArticles }
}
